import * as Yup from "yup";

const passwordValidation = Yup.string()
  .min(8, "Password must be at least 8 characters")
  .max(16, "Password must be at most 16 characters")
  .matches(/[a-z]/, "Password must contain at least one lowercase letter")
  .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
  .matches(/[0-9]/, "Password must contain at least one number")
  .matches(/[@$!%*?&#]/, "Password must contain at least one special character")
  .required("Password is required");

export const loginSchema = Yup.object({
  email: Yup.string().email("Invalid email address").required("Email Required"),
  password: Yup.string().required("Password Required"),
});

export const stockExchangeSchema = Yup.object({
  ticker_name: Yup.string().required("Ticker Name is required"),
  stock_name: Yup.string().required("Stock Exchange Name is required"),
  location: Yup.string().required("Location is required"),
  timezone: Yup.string().required("Timezone is required"),
  opening_time: Yup.string().required("Opening Time is required"),
  closing_time: Yup.string().required("Closing Time is required"),
  status: Yup.string()
    .oneOf(["active", "inactive"], "Invalid status")
    .required("Status is required"),
});

export const masterPlanetSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  stock: Yup.string().required("Ticker name is required"),
  status: Yup.string()
    .oneOf(["active", "inactive"], "Invalid Status")
    .required("Status is required"),
});

export const secondaryPlanetSchema = Yup.object().shape({
  name: Yup.string().required("Name is required"), // Changed from array to string
  ticket_number: Yup.string().required("Ticker Number is required"),
  master_planet: Yup.object()
    .nullable()
    .required("Master Planet is required"),
  status: Yup.string()
    .oneOf(["active", "inactive"])
    .required("Status is required"),
  master: Yup.boolean().required("Master is required"), // Keep this if you're using it
});

export const faqSchema = Yup.object().shape({
  question: Yup.string().required("Question is required"),
  answer: Yup.string().required("Answer is required"),
});

export const contentSchema = Yup.object().shape({
  name: Yup.string().required("Type is required"),
  content: Yup.string().required("Description is required"),
});

export const userSchema = Yup.object().shape({
  name: Yup.string().required("Full Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
});
export const tutorialSchema = Yup.object().shape({
  title: Yup.string().required("Title is required"),
  description: Yup.string().required("Description is required"),
  media_type: Yup.string()
    .oneOf(["video", "pdf"], "Invalid media type")
    .required("Media type is required"),
  media: Yup.string()
    .required("Media is required. Upload file according to selected media type")
    .test(
      "media-validation",
      "Invalid file type for the selected media type",
      function (value) {
        const { media_type } = this.parent;
        const pdfPattern = /\.(pdf)$/i;
        const videoPattern = /\.(mp4|webm|ogg)$/i;

        if (media_type === "pdf") {
          return pdfPattern.test(value);
        } else if (media_type === "video") {
          return videoPattern.test(value);
        }
        return false;
      }
    ),
  paid: Yup.boolean().required("Paid status is required"),
  status: Yup.string()
    .oneOf(["active", "inactive"], "Invalid status")
    .required("Status is required"),
});

export const planSchema = Yup.object().shape({
  name: Yup.string().required("Plan name is required"),
  description: Yup.string()
    .required("Description is required")
    .min(25, "Description must be at least 25 characters long"),
  price: Yup.number()
    .required("Price is required")
    .min(0, "Price must be greater than 0"),
  type: Yup.string()
    .required("Type is required")
    .oneOf(["month", "quarter", "year", "weekly"], "Invalid type"),
  features: Yup.string()
    .required("At least one feature is required")
    .min(1, "Feature cannot be empty")
    .max(2000, "No more than 2000 characters are allowed"),
  duration: Yup.number()
    .integer("Duration must be an integer")
    .min(0, "Duration must be at least 0")
    .default(0),
  free_trial: Yup.string()
    .required("Free trial status is required")
    .oneOf(["yes", "no"], "Invalid free trial status")
    .default("no"),
  free_trial_duration: Yup.number()
    .integer("Free trial duration must be an integer")
    .min(0, "Free trial duration must be at least 0")
    .when("free_trial", {
      is: (value) => value === "yes",
      then: () =>
        Yup.number()
          .required("Free trial duration is required")
          .min(1, "Duration must be at least 1"),
      otherwise: () => Yup.number().notRequired(),
    })
    .default(0),
  status: Yup.string()
    .required("Status is required")
    .oneOf(["active", "inactive", "archived"], "Invalid status")
    .default("active"),
  stripe_price_id: Yup.string().required("Stripe Price ID is required"),
  stripe_product_id: Yup.string().required("Stripe Product ID is required"),
});

export const roleSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  permissions: Yup.array().min(1, "At least one permission is required"),
});

export const addSubAdminSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string().required("Email Number is required").email("Invalid email"),
  password: Yup.string().required("Password is required").matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/, "Password must be at least 8 characters and a mix of uppercase, lowercase letter, a number, and 1 special character"),
  profileImage:Yup.string().required("Profile image is required"),
  phone:Yup.string().required("Mobile number is required"),
  role:Yup.string().required("Role is required"),
  status: Yup.string()
    .oneOf(["active", "inactive"], "Invalid Status")
    .required("Status is required"),
});

export const editSubAdminSchema = Yup.object({
  name: Yup.string().required("Name is required"),
  email: Yup.string().required("Email Number is required").email("Invalid email"),
  profileImage:Yup.string().required("Profile image is required"),
  phone:Yup.string().required("Mobile number is required"),
  role:Yup.string().required("Role is required"),
  status: Yup.string()
    .oneOf(["active", "inactive"], "Invalid Status")
    .required("Status is required"),
});

export const masterPasswordSchema = Yup.object({
  password: Yup.string().required("Master passowrd is required"),
});

export const resetPasswordSchema = Yup.object().shape({
  password: passwordValidation,
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'Passwords must match')
    .required("Confirm Password is required"),
});